import React, { useEffect, useState } from "react";
import { Grid, Box, CircularProgress, Typography, Alert } from "@mui/material";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import KPICard from "./KPICard";
import LineChartWidget from "../Charts/LineChartWidget";
import BarChartWidget from "../Charts/BarChartWidget";
import PieChartWidget from "../Charts/PieChartWidget";
import {
  getSalesRevenueTrends,
  getSalesOrdersByStatus,
  getSalesTopProducts,
  getSalesPaymentMethods,
  getSalesTopClients,
  DateRangeParams,
} from "../../Services/reportsService";
import { Vocabulary } from "../../Utils/Vocabulary";

interface SalesReportsProps {
  dateRange: DateRangeParams;
  onDataChange?: (data: any) => void;
}

const SalesReports: React.FC<SalesReportsProps> = ({ dateRange, onDataChange }) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [revenueTrends, setRevenueTrends] = useState<any>(null);
  const [ordersByStatus, setOrdersByStatus] = useState<any>([]);
  const [topProducts, setTopProducts] = useState<any>([]);
  const [paymentMethods, setPaymentMethods] = useState<any>([]);
  const [topClients, setTopClients] = useState<any>([]);

  useEffect(() => {
    fetchSalesData();
  }, [dateRange]);

  const fetchSalesData = async () => {
    setLoading(true);
    setError(null);

    try {
      const [
        revenueTrendsData,
        ordersByStatusData,
        topProductsData,
        paymentMethodsData,
        topClientsData,
      ] = await Promise.all([
        getSalesRevenueTrends(dateRange),
        getSalesOrdersByStatus(dateRange),
        getSalesTopProducts(dateRange, 10),
        getSalesPaymentMethods(dateRange),
        getSalesTopClients(dateRange, 10),
      ]);

      setRevenueTrends(revenueTrendsData);
      setOrdersByStatus(ordersByStatusData);
      setTopProducts(topProductsData);
      setPaymentMethods(paymentMethodsData);
      setTopClients(topClientsData);

      // Prepare data for export
      if (onDataChange) {
        const revenueData = revenueTrendsData?.data || revenueTrendsData;
        const ordersData = ordersByStatusData?.data || ordersByStatusData;
        const productsData = topProductsData?.data || topProductsData;
        const paymentsData = paymentMethodsData?.data || paymentMethodsData;
        const clientsData = topClientsData?.data || topClientsData;

        const exportData = {
          kpis: {
            totalRevenue: revenueData?.summary?.totalRevenue || 0,
            totalOrders: revenueData?.summary?.totalOrders || 0,
            averageOrderValue: revenueData?.summary?.averageOrderValue || 0,
            topProductsCount: productsData?.length || 0,
          },
          revenueTrends: revenueData?.trends?.map((item: any) => ({
            date: item.period,
            revenue: item.totalRevenue,
            orders: item.orderCount || 0,
          })) || [],
          ordersByStatus: ordersData?.map((item: any) => ({
            status: item.status,
            count: item.count,
            revenue: item.totalRevenue,
          })) || [],
          topProducts: productsData?.map((item: any) => ({
            productName: item.name || item.productName,
            quantitySold: item.quantity || item.quantitySold,
            totalRevenue: item.revenue || item.totalRevenue,
          })) || [],
          paymentMethods: paymentsData?.map((item: any) => ({
            method: item.method || item.paymentMethod,
            count: item.count,
            revenue: item.totalRevenue,
          })) || [],
          topClients: clientsData?.map((item: any) => ({
            clientName: item.name || item.clientName,
            totalRevenue: item.revenue || item.totalRevenue,
            orderCount: item.count || item.orderCount,
          })) || [],
        };
        onDataChange(exportData);
      }
    } catch (err: any) {
      console.error("Error fetching sales data:", err);
      setError(err.message || Vocabulary.errorGeneratingReport);
      if (onDataChange) {
        onDataChange(null);
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 400 }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>{Vocabulary.loadingReport}</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!revenueTrends || !revenueTrends.summary) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">{Vocabulary.noDataAvailable}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* KPI Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title={Vocabulary.totalRevenue}
            value={`${revenueTrends.summary.totalRevenue.toFixed(2)} RON`}
            subtitle={revenueTrends.summary.period}
            icon={<AttachMoneyIcon sx={{ fontSize: 40, color: "#1976d2" }} />}
            color="#1976d2"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title={Vocabulary.totalOrders}
            value={revenueTrends.summary.totalOrders}
            subtitle={Vocabulary.ordersByStatus}
            icon={<ShoppingCartIcon sx={{ fontSize: 40, color: "#2e7d32" }} />}
            color="#2e7d32"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title={Vocabulary.averageOrderValue}
            value={`${revenueTrends.summary.averageOrderValue.toFixed(2)} RON`}
            subtitle={Vocabulary.period}
            icon={<TrendingUpIcon sx={{ fontSize: 40, color: "#ed6c02" }} />}
            color="#ed6c02"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title={Vocabulary.topProducts}
            value={topProducts.length}
            subtitle={Vocabulary.salesSummary}
            icon={<ShoppingCartIcon sx={{ fontSize: 40, color: "#9c27b0" }} />}
            color="#9c27b0"
          />
        </Grid>
      </Grid>

      {/* Charts */}
      <Grid container spacing={3}>
        {/* Revenue Trends Line Chart */}
        <Grid item xs={12} lg={8}>
          <LineChartWidget
            title={Vocabulary.revenueTrends}
            data={revenueTrends.trends}
            xKey="period"
            yKey="totalRevenue"
            yLabel={Vocabulary.totalRevenue}
            color="#1976d2"
            height={350}
          />
        </Grid>

        {/* Orders by Status Pie Chart */}
        <Grid item xs={12} lg={4}>
          <PieChartWidget
            title={Vocabulary.ordersByStatus}
            data={ordersByStatus}
            nameKey="status"
            valueKey="count"
            height={350}
          />
        </Grid>

        {/* Top Products Bar Chart */}
        <Grid item xs={12} lg={6}>
          <BarChartWidget
            title={Vocabulary.topProducts}
            data={topProducts}
            xKey="productName"
            yKey="totalRevenue"
            yLabel={Vocabulary.totalRevenue}
            color="#2e7d32"
            height={350}
          />
        </Grid>

        {/* Payment Methods Pie Chart */}
        <Grid item xs={12} lg={6}>
          <PieChartWidget
            title={Vocabulary.paymentMethods}
            data={paymentMethods}
            nameKey="paymentMethod"
            valueKey="count"
            height={350}
          />
        </Grid>

        {/* Top Clients Bar Chart */}
        <Grid item xs={12}>
          <BarChartWidget
            title={Vocabulary.topClients}
            data={topClients}
            xKey="clientName"
            yKey="totalRevenue"
            yLabel={Vocabulary.totalRevenue}
            color="#9c27b0"
            height={350}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default SalesReports;
